import { Button } from "antd";
import { FormActions } from "../../components/common/FormActions";
import { BooleanTags } from "../../components/common/registerUtils";
import { useGlobalContext } from "../../context/GlobalContext";
import {
  API_CREDIT_HOLDING_TYPES,
  API_MERCHANTS_NUMBER,
  API_REPLENISHMENT,
} from "../../utils/apis";
import { CREDIT_HOLDINGS, FORM_DATA } from "../../utils/constants";
import {
  beautify,
  createEditAction,
  createViewAction,
  formatCurrency,
  formatDateTime,
  getForm,
  getRegister,
  hasWorkflow,
  objectWithUnsetKeys,
} from "../../utils/helpers";
import { URL_CREDIT_HOLDINGS } from "../../utils/urls";
import useSet from "../common/useSet";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { IFormSection } from "../../types/common";
import { useToasts } from "../common/useToasts";
import { useEffect } from "react";
import useGet from "../common/useGet";
import { useParams } from "react-router-dom";

const url = URL_CREDIT_HOLDINGS;

export const useCreditHolding = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id?: string }>();
  const { updateData } = useGlobalContext();
  const {
    uploadData: uploadApprovalData,
    success: approveSuccess,
    error: approveError,
    submitting: approving,
  } = useSet();
  const {
    uploadData: uploadVerifyData,
    success: verifySuccess,
    error: verifyError,
    submitting: verifying,
  } = useSet();
  const {
    uploadData: uploadRejectData,
    success: rejectSuccess,
    error: rejectError,
    submitting: rejecting,
  } = useSet();
  const handleViewAttachment = (url: string) => {
    if (url) {
      updateData({ openAttachment: true, selectedAttachment: url });
    } else {
      toast.error("No attachment URL provided");
    }
  };

  useToasts({
    success: approveSuccess,
    error: approveError,
    submitting: approving,
    successMessage: "Credit request approved successfully",
  });

  useToasts({
    success: verifySuccess,
    error: verifyError,
    submitting: verifying,
    successMessage: "Credit request verified successfully",
  });
  useToasts({
    success: rejectSuccess,
    error: rejectError,
    submitting: rejecting,
    successMessage: "Credit request rejected successfully",
  });

  const handleApprove = async (id: any) => {
    try {
      await uploadApprovalData({
        api: `${API_REPLENISHMENT}${id}/`,
        method: "PATCH",
        params: {
          is_approved: true,
          status: "approved",
        },
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Approval failed");
    }
  };

  const handleVerify = async (id: any) => {
    try {
      await uploadVerifyData({
        api: `${API_REPLENISHMENT}${id}/`,
        method: "PATCH",
        params: {
          status: "verified",
          is_verified: true,
        },
      });

      toast.success("Credit request verified successfully!");
      window.location.reload();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Verification failed");
    }
  };

  const handleReject = async (id: any, reason?: string) => {
    try {
      await uploadRejectData({
        api: `${API_REPLENISHMENT}${id}/`,
        method: "PATCH",
        params: {
          is_rejected: true,
          status: "rejected",
          rejected_reason: reason,
        },
      });
    } catch (error: any) {
      console.error("Rejection Error:", error);
      toast.error(error?.response?.data?.detail || "Rejected failed");
    }
  };

  const tableColumns = () => [
    { id: "merchant_number", header: "Merchant Number", filter: {} },
    { id: "amount", header: "Amount", filter: {} },
    { id: "bank_ref", header: "Bank Ref", filter: {} },
    {
      id: "is_approved",
      header: "Is Approved",
      filter: {
        type: "select",
        placeholder: "Select application type status...",
        options: [
          { id: "false", name: "False" },
          { id: "true", name: "True" },
        ],
      },
    },
    { id: "created_at", header: "Created At", filter: {} },
    { id: "status", header: "Status", filter: {} },
    // { id: "approved_by", header: "Approved By", filter: {} },
    // { id: "approved_at", header: "Approved At", filter: {} },
    // { id: "rejected_reason", header: "Rejection Reason", filter: {} },
    // { id: "rejected_at", header: "Rejected At", filter: {} },
    { id: "proof_of_payment", header: "Receipt" },
    { id: "actions", header: "Actions" },
  ];
  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        is_approved: <BooleanTags status={item?.is_approved} />,
        amount: formatCurrency(item?.amount),
        created_at: formatDateTime(item?.created_at),
        approved_by: beautify(item?.approved_by),
        approved_at: item?.approved_at
          ? formatDateTime(item?.approved_at)
          : "N/A",
        rejected_at: beautify(item?.approved_by),
        proof_of_payment: item.proof_of_payment ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Button
              type="primary"
              style={{
                marginBottom: 4,
                width: "fit-content",
                paddingLeft: 10,
                paddingRight: 10,
              }}
              size="small"
              onClick={() => handleViewAttachment(item?.proof_of_payment)}
            >
              View Receipt
            </Button>
          </div>
        ) : (
          "N/A"
        ),
        actions: (
          <FormActions
            actions={[
              ...(hasWorkflow(user?.workflows, CREDIT_HOLDINGS, 1)
                ? [createEditAction(url, item?.id)]
                : []),
              createViewAction(url, item?.id),
              ...(!item?.is_verified &&
              item?.status === "pending" &&
              hasWorkflow(user?.workflows, CREDIT_HOLDINGS, 2)
                ? [
                    {
                      label: "Reject",
                      custom: true,
                      showReasonField: true,
                      deleteMessage:
                        "Please provide a reason for rejecting this credit.",
                      handleCustom: (reason?: string) =>
                        handleReject(item.id, reason),
                    },
                    {
                      label: "Verify",
                      custom: true,
                      showReasonField: false,
                      handleCustom: () => handleVerify(item.id),
                    },
                  ]
                : []),
              ...(!item?.is_approved &&
              item?.status === "verified" &&
              hasWorkflow(user?.workflows, CREDIT_HOLDINGS, 3)
                ? [
                    {
                      label: "Reject",
                      custom: true,
                      showReasonField: true,
                      deleteMessage:
                        "Please provide a reason for rejecting this credit.",
                      handleCustom: (reason?: string) =>
                        handleReject(item.id, reason),
                    },
                    {
                      label: "Approve",
                      custom: true,
                      showReasonField: false,
                      handleCustom: () => handleApprove(item.id),
                    },
                  ]
                : []),
            ]}
          />
        ),
      };
    }),
  ];
  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Credit Holding Details",
          fields: {
            merchant_number: item?.merchant_number,
            amount: formatCurrency(item?.amount),
            bank_ref: item?.bank_ref,
            receipt_ref: item?.receipt_ref,
            rejected_reason: item?.rejected_reason,
            is_approved: item?.is_approved,
            approved_by: item?.approved_by,
            approved_at: item?.approved_at ? item?.approved_at : "N/A",
            Status: <BooleanTags status={item?.status} />,
            created_at: formatDateTime(item?.created_at),
            created_by: item?.created_by_name,
            initiated_by: item?.initiated_by,
          },
        },
        {
          title: "Proof of Payment",
          fields: {
            proof_of_payment: item?.proof_of_payment ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <Button
                  type="primary"
                  style={{
                    marginBottom: 4,
                    width: "fit-content",
                    paddingLeft: 10,
                    paddingRight: 10,
                  }}
                  size="small"
                  onClick={() => handleViewAttachment(item?.proof_of_payment)}
                >
                  View Receipt
                </Button>
              </div>
            ) : (
              "N/A"
            ),
          },
        },
      ],
    };
  };

  const formRoute = hasWorkflow(user?.workflows, CREDIT_HOLDINGS, 1)
    ? getForm(url)
    : null;

  const endpoint = `${API_REPLENISHMENT}${id}/logs`;

  return { tableData, tableColumns, formRoute, viewData, endpoint };
};

export const useCreditHoldingForm = ({ id }) => {
  const { loadData, loading, data } = useGet();
  const {
    data: creditHoldingTypes,
    loadData: loadCreditHoldingTypes,
    loading: creditHoldingTypesLoading,
    error: creditHoldingTypesError,
  } = useGet();

  useEffect(() => {
    loadCreditHoldingTypes({ api: API_CREDIT_HOLDING_TYPES });
  }, []);

  useEffect(() => {
    loadData({ api: API_MERCHANTS_NUMBER });
  }, []);
  const isEdit = !!id;
  const sections: IFormSection[] = [
    {
      title: "Credit Holding Details",
      fields: [
        {
          field_name: "merchant_number",
          label: "Merchant Number",
          type: "select",
          options_data: data,
          loading,
          props: { required: !isEdit },
        },
        {
          field_name: "currency",
          type: "select",
          props: { required: !isEdit },
          options_data: [{ id: "UGX", name: "UGX" }],
        },
        {
          field_name: "amount",
          type: "text",
          props: { required: !isEdit },
        },
        {
          field_name: "bank_ref",
          type: "text",
          props: { required: !isEdit },
        },
        {
          field_name: "receipt_ref",
          type: "text",
          props: { required: !isEdit },
        },
        {
          field_name: "credit_holding_type",
          label: "Credit Holding Type",
          type: "select",
          props: { required: true },
          options_data: creditHoldingTypes?.results?.map((type: any) => {
            return {
              id: type.type_code,
              name: type.credit_holding_type,
            };
          }),
          options_name: "credit_holding_types",
        },
      ],
    },
    {
      title: "  Receipt",
      fields: [
        {
          field_name: "proof_of_payment",
          label: "Upload proof of payment",
          type: "file",
          props: {
            required: !isEdit,
            placeholder: "Upload statement file",
          },
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  function onSubmit(payload: any, id: string | number) {
    const params = {
      ...payload,
      proof_of_payment:
        Array.isArray(payload?.proof_of_payment?.file) &&
        payload?.proof_of_payment?.file.length > 0
          ? payload?.proof_of_payment?.file[0]
          : [],
      ...objectWithUnsetKeys(payload, ["proof_of_payment"]),
    };
    if (id) {
      return {
        id,
        api: `${API_REPLENISHMENT}`,
        params: params,
        type: FORM_DATA,
      };
    }

    //   CREATE
    return {
      api: `${API_REPLENISHMENT}`,
      params: params,
      type: FORM_DATA,
    };
  }

  return {
    sections,
    redirect,
    onSubmit,
    creditHoldingTypesLoading,
    creditHoldingTypesError,
  };
};
